import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Stat } from "./Primitives";
import { GripVertical } from "lucide-react";
import { KPI_CATALOG, SECTION_ORDER, isKpiAllowedForRole, sectionFor, sectionLabelFor } from "../lib/dashboardPrefs";

/**
 * Compute the numeric value + accent for a KPI id given the loaded payloads.
 */
function kpiValueFor(id, { stats, metrics }) {
  const meta = KPI_CATALOG[id];
  if (!meta) return { value: "—" };
  switch (id) {
    case "active_sites":       return { value: stats?.total_sites ?? "—", accent: "#0A84FF" };
    case "in_progress_shifts": return { value: stats?.in_progress_shifts ?? "—", accent: "#FF9F0A" };
    case "open_incidents":     return { value: stats?.open_incidents ?? "—", accent: "#FF3B30" };
    case "monthly_revenue":    return { value: stats ? `$${Math.round(stats.monthly_revenue || 0).toLocaleString()}` : "—" };
    case "active_guards":      return { value: stats?.active_guards ?? "—", accent: "#34C759" };
    case "total_guards":       return { value: stats?.total_guards ?? "—" };
    case "coverage_pct": {
      const v = metrics?.coverage_fulfillment_pct;
      return { value: v != null ? `${v}%` : "—", accent: v != null ? (v >= 95 ? "#34C759" : v >= 85 ? "#FF9F0A" : "#FF3B30") : undefined };
    }
    case "unrealized_revenue": {
      const v = metrics?.unrealized_revenue;
      return { value: v != null ? `$${Math.round(v).toLocaleString()}` : "—", accent: (v || 0) > 0 ? "#FF3B30" : undefined };
    }
    case "unrealized_hours":   return { value: metrics?.unrealized_hours != null ? Number(metrics.unrealized_hours).toFixed(1) : "—", suffix: "hrs" };
    case "tardy_pct": {
      const v = metrics?.tardy_pct;
      return { value: v != null ? `${v}%` : "—", accent: v == null ? undefined : v > 15 ? "#FF3B30" : v > 5 ? "#FF9F0A" : "#34C759" };
    }
    case "early_out_pct": {
      const v = metrics?.early_clock_out_pct;
      return { value: v != null ? `${v}%` : "—", accent: v == null ? undefined : v > 15 ? "#FF3B30" : v > 5 ? "#FF9F0A" : "#34C759" };
    }
    case "no_show_count": {
      const n = Array.isArray(metrics?.no_show_guards) ? metrics.no_show_guards.length : null;
      return { value: n != null ? n : "—", accent: (n || 0) > 0 ? "#FF3B30" : undefined };
    }
    case "incidents_all":      return { value: metrics?.incidents_all ?? "—" };
    case "maintenance_all":    return { value: metrics?.maintenance_all ?? "—" };
    case "tours_scanned":      return { value: metrics?.tours_scanned ?? "—" };
    case "supervisor_visits":  return { value: metrics?.supervisor_visits ?? "—" };
    case "missed_tour_alerts_today": {
      const n = stats?.missed_tour_alerts_today;
      return {
        value: n != null ? n : "—",
        accent: (n || 0) > 0 ? "#FF3B30" : "#34C759",
      };
    }
    default: return { value: "—" };
  }
}


/**
 * KpiGrid — grouped-by-section KPI grid with HTML5 drag-and-drop across
 * sections. Drag any tile onto another tile in a different section (or onto a
 * section header) to reassign it.
 *
 * Props:
 *   visible               ordered list of KPI ids to render
 *   stats / metrics       payload dicts driving the numbers
 *   role                  gates admin-only tiles
 *   sectionOverrides      per-tile section override map
 *   onReorder(visible)    called with the new order array after any drag
 *   onSectionChange(id, section) called when a tile moves to a new section
 */
export default function KpiGrid({
  visible,
  stats,
  metrics,
  role,
  sectionOverrides = {},
  sectionLabels = {},
  onReorder,
  onSectionChange,
}) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overSection, setOverSection] = useState(null);

  const allowed = useMemo(
    () => visible.filter((id) => isKpiAllowedForRole(id, role)),
    [visible, role],
  );

  // Group tiles by effective section, preserving `visible` order within each group.
  const groups = useMemo(() => {
    const bySection = new Map(SECTION_ORDER.map((s) => [s, []]));
    allowed.forEach((id) => {
      const sec = sectionFor(id, sectionOverrides);
      if (!bySection.has(sec)) bySection.set(sec, []);
      bySection.get(sec).push(id);
    });
    return Array.from(bySection.entries())
      .filter(([, list]) => list.length > 0 || SECTION_ORDER.includes(bySection));
  }, [allowed, sectionOverrides]);

  const handleDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch { /* no-op */ }
  };
  const handleDragEnd = () => { setDragId(null); setOverId(null); setOverSection(null); };

  const handleTileDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };
  const handleTileDrop = (targetId, targetSection) => (e) => {
    e.preventDefault();
    const source = dragId;
    handleDragEnd();
    if (!source || source === targetId) return;
    // Cross-section: reassign section first
    if (sectionFor(source, sectionOverrides) !== targetSection && typeof onSectionChange === "function") {
      onSectionChange(source, targetSection);
    }
    // Reorder: move source to immediately before target in the visible list
    if (typeof onReorder === "function") {
      const next = [...visible];
      const from = next.indexOf(source);
      const to   = next.indexOf(targetId);
      if (from < 0 || to < 0) return;
      next.splice(from, 1);
      next.splice(to, 0, source);
      onReorder(next);
    }
  };

  const handleHeaderDragOver = (section) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (section !== overSection) setOverSection(section);
  };
  const handleHeaderDrop = (section) => (e) => {
    e.preventDefault();
    const source = dragId;
    handleDragEnd();
    if (!source) return;
    if (sectionFor(source, sectionOverrides) !== section && typeof onSectionChange === "function") {
      onSectionChange(source, section);
    }
    // Move to end of the target section within the visible array.
    if (typeof onReorder === "function") {
      const next = [...visible];
      const from = next.indexOf(source);
      if (from < 0) return;
      next.splice(from, 1);
      let insertAt = next.length;
      for (let i = next.length - 1; i >= 0; i -= 1) {
        const sec = i === next.length - 1
          ? sectionFor(next[i], sectionOverrides)
          : sectionFor(next[i], sectionOverrides);
        // Find the last tile in `section` and insert right after it.
        if (sec === section) { insertAt = i + 1; break; }
        insertAt = 0;
      }
      // If no tile currently sits in that section, append.
      const hasAny = next.some((id) => sectionFor(id, sectionOverrides) === section);
      if (!hasAny) insertAt = next.length;
      next.splice(insertAt, 0, source);
      onReorder(next);
    }
  };

  if (allowed.length === 0) {
    return (
      <div
        data-testid="dashboard-kpi-empty"
        className="border border-dashed border-[#27272A] p-6 text-center text-zinc-500 text-sm"
      >
        No KPI tiles enabled. Click <span className="text-zinc-200 font-bold">Customize</span> to
        pick which metrics show here.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-kpi-grid">
      {SECTION_ORDER.map((section) => {
        const ids = (groups.find(([s]) => s === section) || [null, []])[1];
        const isSectionOver = overSection === section && dragId && sectionFor(dragId, sectionOverrides) !== section;
        return (
          <div
            key={section}
            data-testid={`dashboard-kpi-section-${section.toLowerCase()}`}
          >
            <div
              onDragOver={handleHeaderDragOver(section)}
              onDragLeave={() => setOverSection(null)}
              onDrop={handleHeaderDrop(section)}
              className={`flex items-center gap-3 mb-3 py-1 px-2 transition-colors border-l-2 ${
                isSectionOver
                  ? "border-[#FF3B30] bg-[#FF3B30]/10"
                  : "border-[#27272A]"
              }`}
              data-testid={`dashboard-kpi-section-header-${section.toLowerCase()}`}
            >
              <div className="font-display uppercase tracking-widest text-xs text-[#FF3B30] font-bold">
                {sectionLabelFor(section, sectionLabels)}
              </div>
              <div className="label-mute">· {ids.length} tile{ids.length === 1 ? "" : "s"}</div>
              {isSectionOver && (
                <div className="ml-auto text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold animate-pulse">
                  Drop to move here
                </div>
              )}
            </div>
            {ids.length === 0 ? (
              <div
                onDragOver={handleHeaderDragOver(section)}
                onDragLeave={() => setOverSection(null)}
                onDrop={handleHeaderDrop(section)}
                data-testid={`dashboard-kpi-section-empty-${section.toLowerCase()}`}
                className="border border-dashed border-[#27272A] py-6 text-center text-zinc-500 text-xs italic"
              >
                Drop a tile here to move it into {sectionLabelFor(section, sectionLabels)}.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {ids.map((id) => {
                  const meta = KPI_CATALOG[id];
                  const { value, suffix, accent } = kpiValueFor(id, { stats, metrics });
                  const isDragging = dragId === id;
                  const isOver = overId === id && dragId && dragId !== id;
                  const tileInner = (
                    <>
                      <div
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-70 transition-opacity pointer-events-none z-10"
                        aria-hidden
                      >
                        <GripVertical className="size-4 text-zinc-500" />
                      </div>
                      <Stat label={meta.label} value={value} suffix={suffix} accent={accent} />
                    </>
                  );
                  return (
                    <div
                      key={id}
                      data-testid={`dashboard-kpi-${id}`}
                      draggable
                      onDragStart={handleDragStart(id)}
                      onDragOver={handleTileDragOver(id)}
                      onDragLeave={() => setOverId(null)}
                      onDrop={handleTileDrop(id, section)}
                      onDragEnd={handleDragEnd}
                      className={`relative group cursor-grab active:cursor-grabbing transition-all ${
                        isDragging ? "opacity-40 scale-[0.98]" : ""
                      } ${isOver ? "ring-2 ring-[#FF3B30] ring-offset-2 ring-offset-[#0A0A0A]" : ""}`}
                    >
                      {meta.linkTo ? (
                        <Link
                          to={meta.linkTo}
                          data-testid={`dashboard-kpi-${id}-link`}
                          className="block hover:opacity-90 transition-opacity"
                        >
                          {tileInner}
                        </Link>
                      ) : (
                        tileInner
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
